// Chord synonyms: pick any catalogue chord and see every other name that voices
// the exact same pitch-class set — with the shared grip to prove it.

import { useMemo, useState } from 'react';
import { chordSymbol, type ChordTypeId } from '@/music/chords';
import { pitchClassOf, spellNote, type Accidental } from '@/music/notes';
import { chordPitchClasses, findSynonyms, synonymClasses, type ChordName } from '@/music/synonyms';
import { generateChordVoicing } from '@/music/voicing';
import { primaryVoicingFamily } from '@/music/voicingFamilies';
import { getChordPlayer } from '@/audio/player';
import { useStore } from '@/state/store';
import { ChordTypeSelect, NoteSelect } from '@/components/pickers';
import { PlayableDiagram } from '@/components/PlayableDiagram';
import { PitchClassClock } from './PitchClassClock';

const ACCIDENTAL: Accidental = 'flat';

function nameSymbol(name: ChordName): string {
  return chordSymbol(spellNote(name.rootPc, ACCIDENTAL), name.typeId);
}

/** A diagram card for one chord name in the selected family and home layout. */
function NameCard({ name, caption }: { name: ChordName; caption: string }) {
  const stringSet = useStore((s) => s.stringSet);
  const voicingFamily = primaryVoicingFamily(useStore((s) => s.voicingFamily));
  const root = spellNote(name.rootPc, ACCIDENTAL);
  const fingering = useMemo(() => {
    for (const position of [0, 1, 2, 3] as const) {
      const candidate = generateChordVoicing(root, name.typeId, position, stringSet, voicingFamily);
      if (candidate) return candidate;
    }
    return null;
  }, [root, name.typeId, stringSet, voicingFamily]);
  if (!fingering) return null;
  return (
    <PlayableDiagram
      chord={{
        fingering,
        rootDisplay: root,
        chordType: name.typeId,
        symbol: nameSymbol(name),
        inversion: fingering.inversion,
        stringSet,
      }}
      caption={caption}
      showTitle
      variant="card"
    />
  );
}

export function SynonymsExhibit() {
  const [root, setRoot] = useState('C');
  const [typeId, setTypeId] = useState<ChordTypeId>('maj9');

  const rootPc = pitchClassOf(root);
  const pcs = useMemo(() => chordPitchClasses(rootPc, typeId), [rootPc, typeId]);
  const synonyms = useMemo(() => findSynonyms(rootPc, typeId), [rootPc, typeId]);
  const classes = useMemo(() => synonymClasses().slice(0, 6), []);

  const playSet = () => {
    void getChordPlayer().playMidi(
      pcs.map((pc) => 48 + pc),
      0.04,
    );
  };

  return (
    <div className="lab-exhibit" data-testid="lab-synonyms">
      <p className="lab-lede">
        Ninth chords in this app drop their root, so <strong>Cmaj9</strong> is spelled 3-5-7-9:
        E&nbsp;G&nbsp;B&nbsp;D, the same notes as Em7. The grip is the same and only the name
        differs. In set theory this is one pitch-class set with different roots.
      </p>

      <div className="lab-controls">
        <NoteSelect value={root} onChange={setRoot} aria-label="Synonym root" />
        <ChordTypeSelect value={typeId} onChange={setTypeId} aria-label="Synonym chord type" />
      </div>

      <div className="lab-row">
        <PitchClassClock
          pcs={pcs}
          accidental={ACCIDENTAL}
          title={chordSymbol(spellNote(rootPc, ACCIDENTAL), typeId)}
          onTap={playSet}
        />
        <div className="lab-panel">
          <div className="lab-panel-title">
            {pcs.map((pc) => spellNote(pc, ACCIDENTAL)).join(' · ')}
          </div>
          {synonyms.length === 0 ? (
            <p className="muted">No other chord name has exactly these notes.</p>
          ) : (
            <p>
              {synonyms.length === 1 ? 'Also named' : `${synonyms.length} other names`}:{' '}
              {synonyms.map((s) => (
                <strong key={`${s.rootPc}-${s.typeId}`} className="lab-chip-inline">
                  {nameSymbol(s)}
                </strong>
              ))}
              Tap the clock to hear the notes.
            </p>
          )}
        </div>
      </div>

      <div className="lab-cards" data-testid="synonym-cards">
        <NameCard name={{ rootPc, typeId }} caption="as named" />
        {synonyms.map((s) => (
          <NameCard key={`${s.rootPc}-${s.typeId}`} name={s} caption="same notes" />
        ))}
      </div>

      <h3>Largest groups</h3>
      <p className="muted">
        Sets ranked by how many names they have. The largest is the diminished seventh: four °7
        names and four 7♭9 names on one set of four notes (Barry Harris&rsquo;s family of four
        dominants).
      </p>
      <div className="lab-chip-row">
        {classes.map((cls) => {
          const first = cls.names[0];
          return (
            <button
              key={cls.pcs.join(',')}
              className="btn btn-sm"
              onClick={() => {
                setRoot(spellNote(first.rootPc, ACCIDENTAL));
                setTypeId(first.typeId);
              }}
            >
              {cls.names.map(nameSymbol).join(' = ')}
            </button>
          );
        })}
      </div>
    </div>
  );
}
